import { useState } from 'react';
import { Link, useParams } from 'react-router';
import {
  Button,
  Card,
  EmptyState,
  LoadingRegion,
  Notice,
  Status,
  VENUE_MAP_VERSION_STATES,
  presentation,
} from '@gatecheckpro/design-system';
import type { VenueMapDetail, VenueMapVersionSummary } from '@gatecheckpro/contracts';
import { useCommand } from '../../lib/useCommand.ts';
import { useResource } from '../../lib/useResource.ts';
import { FailureNotice } from '../../lib/refusal.tsx';

/**
 * One map, and every version of it it has ever had.
 *
 * The page exists to make one rule legible: a version stops moving when it is
 * approved. Correcting an approved room means opening a new draft from it, and
 * the version it came from is never touched — because an event configured
 * against last season's map must keep resolving to the room that was actually
 * sold (ADR 0036).
 */
export function VenueMapDetailPage() {
  const { organizationId, venueMapId } = useParams<{
    organizationId: string;
    venueMapId: string;
  }>();
  const base = `/api/v1/organizer/organizations/${organizationId ?? ''}`;
  const { resource, reload } = useResource<VenueMapDetail>(
    `${base}/venue-maps/${venueMapId ?? ''}`,
  );
  const command = useCommand<VenueMapDetail>();

  if (resource.status === 'loading') return <LoadingRegion label="Loading map" />;
  if (resource.status === 'failed') return <FailureNotice error={resource.error} />;

  return (
    <div className="gc-stack gc-stack--loose">
      <FailureNotice error={command.error} />
      <VenueMapDetailView
        map={resource.data}
        organizationId={organizationId ?? ''}
        busy={command.busy}
        onStartDraft={(cloneFromVersionId) => {
          void command
            .run(`${base}/venue-maps/${venueMapId ?? ''}/versions`, {
              method: 'POST',
              body: { cloneFromVersionId },
            })
            .then((result) => {
              if (result !== null) reload();
            });
        }}
        onRetire={() => {
          void command
            .run(`${base}/venue-maps/${venueMapId ?? ''}/retire`, { method: 'POST' })
            .then((result) => {
              if (result !== null) reload();
            });
        }}
      />
    </div>
  );
}

/**
 * The map as a person reads it, separated from the fetch that produced it.
 *
 * Everything here is a judgment about what a state *means*, which is the part
 * worth testing: whether an unapproved map says so, whether a second draft is
 * offered while one is already open, and whether the page ever implies a room
 * is on sale because it has been drawn.
 */
export function VenueMapDetailView({
  map,
  organizationId,
  busy = false,
  onStartDraft,
  onRetire,
}: {
  readonly map: VenueMapDetail;
  readonly organizationId: string;
  readonly busy?: boolean;
  readonly onStartDraft: (cloneFromVersionId: string | null) => void;
  readonly onRetire?: (() => void) | undefined;
}) {
  const [cloneFrom, setCloneFrom] = useState('');
  const retired = map.status === 'RETIRED';
  const approvedVersions = map.versions.filter(
    (version) => version.lifecycle === 'PUBLISHED' || version.lifecycle === 'SUPERSEDED',
  );

  return (
    <>
      <div className="gc-stack gc-stack--tight">
        <span className="gc-eyebrow">
          <Link to={`/o/${organizationId}/venues/${map.venueId}/maps`}>Maps</Link>
        </span>
        <div className="gc-row gc-row--baseline">
          <h2>{map.displayName}</h2>
          {retired && <Status tone="neutral" glyph="−" label="Retired" />}
        </div>
        <p className="gc-muted gc-mono">{map.code}</p>
      </div>

      {retired && (
        <Notice tone="caution" title="This map is retired">
          Retired maps stay readable, because anything already configured against one still means
          this room. Nothing new can be drafted here.
        </Notice>
      )}

      <div className="gc-grid">
        <Card title="Approved" eyebrow="What later configuration resolves to">
          {map.publishedVersion === null ? (
            <p className="gc-muted">
              Nothing approved yet. Until a version is approved, no event can be configured against
              this room.
            </p>
          ) : (
            <VersionFacts version={map.publishedVersion} />
          )}
        </Card>

        <Card title="Open draft" eyebrow="Editable">
          {map.draftVersion === null ? (
            <p className="gc-muted">
              No draft is open. A map may have one open draft at a time, which is what stops two
              corrections to the same room from racing.
            </p>
          ) : (
            <div className="gc-stack gc-stack--tight">
              <VersionFacts version={map.draftVersion} />
              <Link
                to={`/o/${organizationId}/venue-map-versions/${map.draftVersion.venueMapVersionId}`}
              >
                Draw this room
              </Link>
            </div>
          )}
        </Card>
      </div>

      {/*
        Deliberately not a disabled "Edit map" button. The editor is genuinely
        not built, and a greyed-out control invites someone to keep clicking it
        and to conclude the product is broken. No date is offered either: this
        module cannot observe a schedule, and promising one is how the workspace
        ended up telling organizers that events would arrive in Slice 2 for the
        eight tranches after they had.
      */}
      <Notice tone="info" title="Drawing a map is not available in this release">
        Versions can be opened, approved and read here. Placing sections, rows, seats and tables is
        not built yet, so a draft opened now will have no topology in it.
      </Notice>

      {!retired && onRetire !== undefined && (
        <Card title="Retire this map" eyebrow="When a room stops being used">
          <div className="gc-stack gc-stack--tight">
            <p className="gc-muted">
              A retired map stays readable for good, because anything already configured against it
              still means this room. Nothing new can be drafted against it.
            </p>
            <div>
              <Button busy={busy} onClick={onRetire}>
                Retire this map
              </Button>
            </div>
          </div>
        </Card>
      )}

      <Card
        title="Versions"
        eyebrow={`${String(map.versions.length)} in this map's history`}
        action={
          retired ? undefined : (
            <Button
              variant="primary"
              busy={busy}
              disabled={map.draftVersion !== null}
              title={
                map.draftVersion === null
                  ? undefined
                  : 'Publish or discard the open draft before starting another.'
              }
              onClick={() => onStartDraft(cloneFrom === '' ? null : cloneFrom)}
            >
              Start a draft
            </Button>
          )
        }
      >
        <div className="gc-stack">
          {!retired && approvedVersions.length > 0 && (
            <div className="gc-field">
              <label className="gc-field__label" htmlFor="cloneFrom">
                Start it from
              </label>
              <select
                id="cloneFrom"
                className="gc-select"
                value={cloneFrom}
                onChange={(input) => setCloneFrom(input.target.value)}
              >
                <option value="">An empty room</option>
                {approvedVersions.map((version) => (
                  <option key={version.venueMapVersionId} value={version.venueMapVersionId}>
                    Copy of v{version.versionNumber} — {version.seatCount.toLocaleString()} seats
                  </option>
                ))}
              </select>
              <span className="gc-field__hint">
                Copying reads the source version and never changes it. Correcting a room is a new
                version, not an edit to the one people already bought against.
              </span>
            </div>
          )}

          <VenueMapVersionHistory versions={map.versions} organizationId={organizationId} />
        </div>
      </Card>
    </>
  );
}

/** Every version this map has ever had, including the ones it no longer uses. */
export function VenueMapVersionHistory({
  versions,
  organizationId = '',
}: {
  readonly versions: readonly VenueMapVersionSummary[];
  readonly organizationId?: string;
}) {
  if (versions.length === 0) {
    return (
      <EmptyState title="No versions yet">
        A map is a container; its versions are what carry the room. Start a draft to make the first
        one.
      </EmptyState>
    );
  }
  return (
    <div className="gc-table-scroll">
      <table className="gc-table">
        <thead>
          <tr>
            <th scope="col">Version</th>
            <th scope="col">State</th>
            <th scope="col">Seats</th>
            <th scope="col">Canvas</th>
            <th scope="col">Topology</th>
          </tr>
        </thead>
        <tbody>
          {versions.map((version) => {
            const state = presentation(VENUE_MAP_VERSION_STATES, version.lifecycle);
            return (
              <tr key={version.venueMapVersionId}>
                <td className="gc-mono">
                  <Link to={`/o/${organizationId}/venue-map-versions/${version.venueMapVersionId}`}>
                    v{version.versionNumber}
                  </Link>
                </td>
                <td>
                  <Status
                    tone={state.tone}
                    glyph={state.glyph}
                    label={state.label}
                    title={state.meaning}
                  />
                </td>
                <td className="gc-mono">{version.seatCount.toLocaleString()}</td>
                <td className="gc-mono gc-muted">
                  {version.canvas.width} × {version.canvas.height}
                </td>
                {/*
                  Always present now: a draft carries a digest from the moment
                  it is created, including the digest of an empty room. Whether
                  anything has been drawn is the seat count's question, not
                  this column's.
                */}
                <td className="gc-mono gc-muted" title={version.topologyDigest}>
                  {version.topologyDigest.slice(0, 12)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/** The facts about one version that a person actually acts on. */
function VersionFacts({ version }: { readonly version: VenueMapVersionSummary }) {
  const state = presentation(VENUE_MAP_VERSION_STATES, version.lifecycle);
  return (
    <div className="gc-stack gc-stack--tight">
      <div className="gc-row gc-row--baseline">
        <span className="gc-mono">v{version.versionNumber}</span>
        <Status tone={state.tone} glyph={state.glyph} label={state.label} title={state.meaning} />
      </div>
      <p className="gc-muted">{state.meaning}</p>
      <p className="gc-muted">
        {version.seatCount === 0
          ? 'No seats placed.'
          : `${version.seatCount.toLocaleString()} seats.`}
      </p>
    </div>
  );
}
